import styled from 'styled-components';
import { spacing } from 'ui/theme';
import { Badge } from 'ui/badge';
import { SecondaryButton } from 'ui/button';
import { JsonBody } from 'modules/workflowWebhooksIndex/components/JsonBody';
import { WebhookBodyNotSet } from 'modules/workflowWebhooksIndex/components/WebhookBodyNotSet';
import { Detail, DetailHeader, DetailBody } from './WorkflowWebhookDetails.styled';
import type { WorkflowWebhook, KeyValuePair } from '../types';
import { AUTH_TYPES } from '../constants';

type Props = {
  webhook: WorkflowWebhook;
  onEdit: () => void;
};

const Header = styled.div`
  display: flex;
  flex-direction: row;
`;

const Key = styled.div`
  min-width: ${spacing.x12};
  margin-right: ${spacing.x3};
`;

const Value = styled.div`
  flex-grow: 1;
`;

const Endpoint = styled.span`
  margin-left: ${spacing.half};
`;

export const WorkflowWebhookRequest = ({ webhook, onEdit }: Props) => {
  const {
    contentType,
    jsBody,
    method,
    formBody,
    endpoint,
    authType,
    headers,
    includeDefaultFields,
    userFields,
  } = webhook;

  const mergedUserFields = [
    ...(includeDefaultFields ? ['workflowId', 'email'] : []),
    ...userFields.map((f) => f.field),
  ];

  const renderBody = () => {
    switch (contentType) {
      case 'Json':
        return <JsonBody body={jsBody} />;
      case 'FormUrlEncoded':
        if (formBody.length === 0) {
          return <WebhookBodyNotSet />;
        }
        return (
          <Header>
            <Key>
              {formBody.map((kvp: KeyValuePair, idx: number) => (
                <div key={idx}>{kvp.key}</div>
              ))}
            </Key>
            <Value>
              {formBody.map((kvp: KeyValuePair, idx: number) => (
                <div key={idx}>{kvp.value || <WebhookBodyNotSet key={idx} />}</div>
              ))}
            </Value>
          </Header>
        );
      default:
        return <WebhookBodyNotSet />;
    }
  };

  return (
    <>
      <Detail>
        <DetailHeader>Endpoint</DetailHeader>
        <DetailBody>
          <Badge variant="info">{method}</Badge>
          <Endpoint>{endpoint}</Endpoint>
        </DetailBody>
        <DetailHeader>Authentication</DetailHeader>
        {/* @ts-expect-error */}
        <DetailBody>{AUTH_TYPES[authType]}</DetailBody>
        <DetailHeader>Custom Headers</DetailHeader>
        <DetailBody>
          {headers.length > 0 ? (
            headers.map((header: KeyValuePair, idx: number) => (
              <Header key={idx}>
                <Key>{header.key}</Key>
                <Value>{header.value || <WebhookBodyNotSet key={idx} />}</Value>
              </Header>
            ))
          ) : (
            <WebhookBodyNotSet />
          )}
        </DetailBody>
        <DetailHeader>Body</DetailHeader>
        <DetailBody>{renderBody()}</DetailBody>
        {mergedUserFields.length > 0 && (
          <>
            <DetailHeader>Additional fields</DetailHeader>
            <DetailBody>{mergedUserFields.join(', ')}</DetailBody>
          </>
        )}
      </Detail>
      <SecondaryButton onClick={onEdit}>Edit request</SecondaryButton>
    </>
  );
};
